import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { RollingTitles } from '@/components/ui/RollingTitles';
import { PostFetchRecommendation } from '@/components/enforcement/PostFetchRecommendation';
import { useSmoothProgress } from '@/hooks/useSmoothProgress';

export interface SyncStats {
  new: number;
  updated: number;
  deleted: number;
}

interface FetchProgressDialogProps {
  open: boolean;
  productCount: number;
  status: 'connecting' | 'fetching' | 'processing' | 'complete';
  page?: number;
  totalPages?: number;
  syncStats?: SyncStats;
  onClose?: () => void;
  onCancel?: () => void;
  cancelling?: boolean;
  isStale?: boolean;
  feedProductCount?: number | null;
  estimatedProductCount?: number | null;
  recentTitles?: string[];
  /**
   * Der naechste Schritt der Leiter als Primaerknopf (Spec 2026-09-16,
   * Abschnitt 7). Bis dahin bot dieser Dialog nach einem fertigen Sync genau
   * "Schliessen" an -- der Nutzer hatte gerade tausende Produkte geholt und
   * stand vor einer Sackgasse.
   *
   * Als SLOT und nicht als eigener Knopf: das Ziel und die Beschriftung kommen
   * aus `NextStepCta` (und damit aus der Leiter), und dieser Dialog kennt
   * weder Kontingent noch Produktzahlen.
   */
  handoff?: React.ReactNode;
}

// Format remaining time as human-readable string
function formatTimeRemaining(ms: number, locale: string): string {
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) {
    return locale === 'de-DE' ? `< 1 Min` : `< 1 min`;
  }
  const minutes = Math.ceil(totalSeconds / 60);
  return locale === 'de-DE' ? `ca. ${minutes} Min` : `~${minutes} min`;
}

export const FetchProgressDialog: React.FC<FetchProgressDialogProps> = ({
  open,
  productCount,
  status,
  syncStats,
  onClose,
  onCancel,
  cancelling,
  isStale,
  feedProductCount,
  estimatedProductCount,
  recentTitles,
  handoff,
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const isComplete = status === 'complete';
  const isConnecting = status === 'connecting';
  const isFetching = status === 'fetching' || status === 'processing';
  const isReconnecting = !isComplete && isStale === true;
  const isActive = isConnecting || isFetching;

  // Track when fetching started for time estimate
  const fetchStartRef = useRef<number | null>(null);
  useEffect(() => {
    if (isFetching && productCount > 0 && !fetchStartRef.current) {
      fetchStartRef.current = Date.now();
    }
    if (isComplete || !open) {
      fetchStartRef.current = null;
    }
  }, [isFetching, productCount, isComplete, open]);

  // Estimated time remaining
  const [timeRemaining, setTimeRemaining] = useState<string | null>(null);
  useEffect(() => {
    if (!isFetching || !fetchStartRef.current || !estimatedProductCount || estimatedProductCount <= 0 || productCount <= 0) {
      setTimeRemaining(null);
      return;
    }
    const elapsed = Date.now() - fetchStartRef.current;
    // Wait at least 5s for stable rate estimate
    if (elapsed < 5000) return;

    const rate = productCount / elapsed; // products per ms
    const remaining = (estimatedProductCount - productCount) / rate;
    // Add ~30s buffer for merge phase
    const totalRemaining = remaining + 30000;
    if (totalRemaining > 0 && totalRemaining < 3600000) {
      setTimeRemaining(formatTimeRemaining(totalRemaining, locale));
    } else {
      setTimeRemaining(null);
    }
  }, [productCount, isFetching, estimatedProductCount, locale]);

  // Data-driven progress: use real fetched count vs estimated total.
  // Fetch phase = 0-90%, merge phase = 90-100%.
  // Falls back to time-based when no estimate is available (first sync).
  const hasEstimate = estimatedProductCount && estimatedProductCount > 0;
  const dataPercent = hasEstimate && productCount > 0
    ? Math.min(Math.round((productCount / estimatedProductCount) * 90), 90)
    : null;

  // Time-based fallback for first sync (no estimated_product_count yet)
  const smoothPercent = useSmoothProgress(
    isActive && !isReconnecting && !hasEstimate,
    isComplete,
  );

  // Merge-phase progress: when fetch hits 90%, animate slowly toward 98%
  // Merge typically takes 30-60s
  const isMergePhase = !isComplete && dataPercent === 90;
  const mergeSmooth = useSmoothProgress(isMergePhase, isComplete);
  // mergeSmooth goes 0→15 over time; we map that to 90→98
  const mergePercent = isMergePhase ? 90 + (mergeSmooth / 15) * 8 : null;

  const getProgress = (): number | undefined => {
    if (isComplete) return 100;
    if (isReconnecting) return undefined;
    if (isConnecting && !dataPercent) return 2; // small nudge at start

    // Merge phase: slow fill 90→98%
    if (mergePercent !== null) return Math.round(mergePercent);

    // Prefer data-driven progress
    if (dataPercent !== null) return dataPercent;

    // Fallback: time-based
    return smoothPercent;
  };

  const progressPercent = getProgress();
  const hasStats = syncStats && (syncStats.new > 0 || syncStats.updated > 0 || syncStats.deleted > 0);

  const statusMessage = isConnecting
    ? t('fetchProgress.connecting')
    : isReconnecting
      ? t('fetchProgress.reconnecting')
      : isMergePhase
        ? t('fetchProgress.merging')
        : isFetching
          ? t('fetchProgress.downloading')
          : t('fetchProgress.syncComplete');

  const handleOpenChange = (v: boolean) => {
    if (!v && isComplete && onClose) {
      onClose();
    }
  };

  const handleInteractOutside = (e: Event) => {
    if (!isComplete) e.preventDefault();
  };

  const handlePointerDownOutside = (e: Event) => {
    if (!isComplete) e.preventDefault();
  };

  const handleEscapeKeyDown = (e: KeyboardEvent) => {
    if (!isComplete) e.preventDefault();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onInteractOutside={handleInteractOutside}
        onPointerDownOutside={handlePointerDownOutside}
        onEscapeKeyDown={handleEscapeKeyDown}
        hideCloseButton={!isComplete}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isComplete ? (
              <CheckCircle2 className="h-5 w-5 text-success" />
            ) : (
              <Spinner size="md" />
            )}
            {isComplete ? t('fetchProgress.titleComplete') : isReconnecting ? t('fetchProgress.titleReconnecting') : t('fetchProgress.titleFetching')}
          </DialogTitle>
          {/* H3-F2: Radix warnte "Missing Description or aria-describedby"
              — Screenreader bekamen fuer den Dialog keine Beschreibung.
              sr-only, damit die dicht gebaute Fortschrittsflaeche visuell
              unveraendert bleibt. */}
          <DialogDescription className="sr-only">
            {t('fetchProgress.dialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Progress bar with percentage */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {statusMessage}
              </span>
              <span className="flex items-center gap-2">
                {timeRemaining && isFetching && !isMergePhase && (
                  <span className="text-xs text-muted-foreground/70">
                    {timeRemaining}
                  </span>
                )}
                {progressPercent !== undefined && (
                  <span className="font-medium tabular-nums">
                    {Math.round(progressPercent)}%
                  </span>
                )}
              </span>
            </div>
            <Progress
              value={progressPercent}
              variant="gradient"
              smooth
              indeterminate={progressPercent === undefined}
              className="h-3"
            />
          </div>

          {/* Rolling product titles during fetch */}
          {isFetching && !isMergePhase && recentTitles && recentTitles.length > 0 && (
            <div className="rounded-lg border bg-muted/30 px-3 py-1 overflow-hidden">
              <RollingTitles titles={recentTitles} />
            </div>
          )}

          {/* Sync stats breakdown (only after completion) */}
          {hasStats && (
            <div className="grid grid-cols-3 gap-3 pt-2 border-t">
              <div className="text-center space-y-1">
                <div className="flex items-center justify-center gap-1.5">
                  <Plus className="h-4 w-4 text-success" />
                  <span className="text-xl font-bold text-success-ink">{syncStats.new.toLocaleString(locale)}</span>
                </div>
                <p className="text-xs text-muted-foreground">{t('fetchProgress.new')}</p>
              </div>
              <div className="text-center space-y-1">
                <div className="flex items-center justify-center gap-1.5">
                  <RefreshCw className="h-4 w-4 text-info" />
                  <span className="text-xl font-bold text-info-ink">{syncStats.updated.toLocaleString(locale)}</span>
                </div>
                <p className="text-xs text-muted-foreground">{t('fetchProgress.updated')}</p>
              </div>
              <div className="text-center space-y-1">
                <div className="flex items-center justify-center gap-1.5">
                  <Trash2 className="h-4 w-4 text-destructive" />
                  <span className="text-xl font-bold text-destructive">{syncStats.deleted.toLocaleString(locale)}</span>
                </div>
                <p className="text-xs text-muted-foreground">{t('fetchProgress.deleted')}</p>
              </div>
            </div>
          )}

          {/* Total products synced (shown after completion) */}
          {isComplete && productCount > 0 && (
            <p className="text-sm text-center text-muted-foreground">
              {productCount.toLocaleString(locale)} {t('fetchProgress.productsSynced')}
            </p>
          )}

          {isComplete && feedProductCount === 0 && productCount > 0 && (
            <div className="flex gap-2.5 rounded-lg border border-warning/30 bg-warning-light p-3">
              <AlertTriangle className="h-5 w-5 text-warning-ink shrink-0 mt-0.5" />
              <p className="text-sm text-warning-ink">
                {t('fetchProgress.emptyFeedWarning')}
              </p>
            </div>
          )}

          {isComplete && (feedProductCount === null || feedProductCount > 0) && <PostFetchRecommendation />}

          {/* Mit Uebergabe ist SIE der Primaerknopf und "Schliessen" der
              Nebenweg; ohne sie bleibt alles wie bisher. */}
          {isComplete && handoff && <div className="mt-2">{handoff}</div>}

          {isComplete && onClose && (
            <Button
              onClick={onClose}
              variant={handoff ? 'outline' : 'default'}
              className="w-full mt-2"
            >
              {t('fetchProgress.close')}
            </Button>
          )}

          {isFetching && onCancel && (
            <Button
              onClick={onCancel}
              variant="outline"
              disabled={cancelling}
              className="w-full mt-2"
            >
              {cancelling ? t('fetchProgress.cancelling') : t('fetchProgress.cancel')}
            </Button>
          )}

          {isFetching && !onCancel && (
            <p className="text-xs text-muted-foreground text-center">
              {t('fetchProgress.pleaseWait')}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
